import type { SchemaField } from '@cli-types/core-engine/schema.js'
import type { FieldRenderContext } from '@cli-types/next-adapter/forms.js'
import { formItemProps } from '@next-adapter/generators/form/form-shared/form-item-props.js'
import { formLabel } from '@next-adapter/generators/form/form-shared/form-label.js'
import { labelWithDescription } from '@next-adapter/generators/form/form-shared/label-with-description.js'
import { renderSelectFieldRenderer } from '@next-adapter/templates/renderers/generators/form/field-jsx/render-select-field.js'
import { defaultValueFromSourceHandler } from './default-value-from-source-handler.js'
import { renderCreatableSelectField } from './render-creatable-select-field.js'
import { renderMultiSelectField } from './render-multi-select-field.js'

export function renderSelectField(
  field: SchemaField,
  indent: string,
  label: string,
  hintJSX: string,
  context: FieldRenderContext
): string {
  if (field.multiple) {
    return renderMultiSelectField(field, indent, label, hintJSX, context)
  }

  if (field.creatable && context.creatableSelectFieldNames?.has(field.name)) {
    return renderCreatableSelectField(field, indent, label, hintJSX, context)
  }

  const options = field.options || []
  const defaultValueFromHandler = defaultValueFromSourceHandler(field, context)
  const onValueChange = defaultValueFromHandler
    ? renderSelectFieldRenderer({
        kind: 'renderSelectFieldChangeHandlerTemplate',
        props: {
          indent: indent,
          defaultValueFromHandler: defaultValueFromHandler
        }
      })
    : 'formField.onChange'
  const placeholder = field.placeholder || `Select ${label.toLowerCase()}`
  const selectedLabelExpression = options.reduceRight(
    (fallback, option) =>
      `formField.value === ${JSON.stringify(option.value)} ? ${JSON.stringify(option.label)} : ${fallback}`,
    'undefined'
  )
  const validValuesExpression = `([${options.map((option) => JSON.stringify(option.value)).join(', ')}] as const as readonly string[])`
  const optionsJSX = options
    .map(
      (o) =>
        `${indent}          <SelectItem key="${o.value}" value="${o.value}">${o.label}</SelectItem>`
    )
    .join('\n')
  return renderSelectFieldRenderer({
    kind: 'renderSelectFieldFormFieldTemplate',
    props: {
      indent: indent,
      fieldName: field.name,
      formItemProps: formItemProps(field),
      labelWithDescription: labelWithDescription(
        formLabel(field, label),
        hintJSX,
        `${indent}      `
      ),
      validValuesExpression: validValuesExpression,
      onValueChange: onValueChange,
      selectedLabelExpression: selectedLabelExpression,
      stringify: JSON.stringify(placeholder),
      optionsJsx: optionsJSX
    }
  })
}
